
import React, { useState, useEffect } from "react";
import { CartItem } from "@/types/cart";
import { Card } from "@/components/ui/card";
import { Trash2, Minus, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";

interface ExtraOption {
  id: string;
  name: string;
  price: number;
}

const defaultExtrasOptions: ExtraOption[] = [
  { id: "catupiry", name: "Catupiry", price: 5.70 },
  { id: "cheddar", name: "Cheddar", price: 5.70 },
  { id: "4queijos", name: "4 Queijos", price: 5.70 },
  { id: "cebola", name: "Cebola Grelhada", price: 5.50 },
  { id: "queijo", name: "Queijo extra", price: 7.00 },
  { id: "azeitonas", name: "Azeitonas", price: 5.00 },
  { id: "palha", name: "Batata palha", price: 4.00 },
  { id: "bacon", name: "Bacon", price: 19.00 },
  { id: "ovo", name: "Ovo extra", price: 4.50 }
];

interface CartSummaryDetailProps {
  cart: CartItem[];
  updateQuantity: (productId: string, quantity: number) => void;
  removeFromCart: (productId: string) => void;
  updateNotes: (productId: string, notes: string) => void;
  updateProductExtras: (productId: string, extras: any[]) => void;
  getTotalPrice: () => number;
}

const CartSummaryDetail: React.FC<CartSummaryDetailProps> = ({
  cart,
  updateQuantity,
  removeFromCart,
  updateNotes,
  updateProductExtras,
  getTotalPrice
}) => {
  const [extrasOptions, setExtrasOptions] = useState<ExtraOption[]>(defaultExtrasOptions);
  const [loadingExtras, setLoadingExtras] = useState(true);

  // Carregar adicionais ativos cadastrados no banco de dados (Painel Administrativo)
  useEffect(() => {
    const fetchExtrasFromDb = async () => {
      try {
        const { data, error } = await supabase
          .from("products")
          .select("id, name, price, is_active")
          .ilike("category", "%adicionai%")
          .eq("is_active", true)
          .order("name", { ascending: true });

        if (!error && data && data.length > 0) {
          const loadedExtras: ExtraOption[] = data.map((item) => ({
            id: item.id,
            name: item.name.trim(),
            price: typeof item.price === "number" ? item.price : parseFloat(item.price) || 0
          }));

          setExtrasOptions(loadedExtras);

          // Atualizar os adicionais que já estão selecionados no carrinho com os novos preços do banco
          cart.forEach((item) => {
            if (item.extras && item.extras.length > 0) {
              let hasChanged = false;
              const updatedExtras = item.extras.map((extra) => {
                const fresh = loadedExtras.find(
                  (e) => e.id === extra.id || e.name.toLowerCase() === extra.name.toLowerCase()
                );
                if (fresh && (fresh.price !== extra.price || fresh.name !== extra.name)) {
                  hasChanged = true;
                  return { ...extra, id: fresh.id, name: fresh.name, price: fresh.price };
                }
                return extra;
              });

              if (hasChanged) {
                updateProductExtras(item.product.id, updatedExtras);
              }
            }
          });
        }
      } catch (err) {
        console.error("Erro ao carregar adicionais do banco de dados:", err);
      } finally {
        setLoadingExtras(false);
      }
    };

    fetchExtrasFromDb();
  }, []);

  // Calculate item price including customizations and extras
  const calculateItemPrice = (item: CartItem) => {
    const basePrice = item.product.price * item.quantity;
    const customizationsPrice = Array.isArray(item.customizations) ? item.customizations.reduce((total, cust) => total + cust.price * item.quantity, 0) : 0;
    const extrasPrice = Array.isArray(item.extras) ? item.extras.reduce((total, extra) => total + extra.price * item.quantity, 0) : 0;
    return basePrice + customizationsPrice + extrasPrice;
  };

  const handleExtraToggle = (productId: string, extraId: string) => {
    const item = cart.find(cartItem => cartItem.product.id === productId);
    if (!item) return;

    const currentExtras = item.extras || [];
    const extra = extrasOptions.find(e => e.id === extraId);
    if (!extra) return;

    const isSelected = currentExtras.some(e => e.id === extraId || e.name.toLowerCase() === extra.name.toLowerCase());
    let newExtras;

    if (isSelected) {
      newExtras = currentExtras.filter(e => e.id !== extraId && e.name.toLowerCase() !== extra.name.toLowerCase());
    } else {
      newExtras = [...currentExtras, extra];
    }

    updateProductExtras(productId, newExtras);
  };

  // Check if a product is a lanche or xis
  const isLancheProduct = (category: string = "") => {
    const cat = category.toLowerCase();
    return cat.includes('lanche') || 
           cat.includes('xis') ||
           cat.includes('hamburger') ||
           cat.includes('sanduiche');
  };

  console.log("Cart items:", cart);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold mb-4 text-foreground">Seu Pedido</h2>

      {cart.length === 0 ? (
        <Card className="p-6 text-center bg-card border-border">
          <p className="text-muted-foreground">Seu carrinho está vazio</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {cart.map(item => (
            <Card key={item.product.id} className="bg-card text-card-foreground border-border shadow-sm">
              <div className="p-4">
                <div className="flex gap-4">
                  <div className="w-20 h-20 relative rounded-md overflow-hidden bg-muted border border-border">
                    <img src={item.product.image_url} alt={item.product.name} className="w-full h-full object-cover" />
                  </div>

                  <div className="flex-1">
                    <div className="flex justify-between">
                      <h3 className="font-medium text-lg text-foreground">{item.product.name}</h3>
                      <button onClick={() => removeFromCart(item.product.id)} className="text-red-500 hover:text-red-600 transition-colors">
                        <Trash2 size={18} />
                      </button>
                    </div>

                    <p className="text-muted-foreground text-sm mb-2">
                      R$ {item.product.price.toFixed(2).replace(".", ",")} / unidade
                    </p>

                    <div className="flex items-center gap-2 mt-1">
                      <Button variant="outline" size="icon" className="h-7 w-7 p-0 border-border hover:bg-accent text-foreground" onClick={() => updateQuantity(item.product.id, Math.max(1, item.quantity - 1))}>
                        <Minus size={14} />
                      </Button>
                      <span className="w-8 text-center text-foreground font-medium">{item.quantity}</span>
                      <Button variant="outline" size="icon" className="h-7 w-7 p-0 border-border hover:bg-accent text-foreground" onClick={() => updateQuantity(item.product.id, item.quantity + 1)}>
                        <Plus size={14} />
                      </Button>
                      <span className="ml-auto font-medium text-foreground">
                        R$ {calculateItemPrice(item).toFixed(2).replace(".", ",")}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Exibir personalizações */}
                {Array.isArray(item.customizations) && item.customizations.length > 0 && (
                  <div className="mt-3 pl-24 space-y-1">
                    <p className="text-sm text-muted-foreground">Personalizações:</p>
                    {item.customizations.map((customization, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className={customization.type === 'adicionar' ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}>
                          {customization.type === 'adicionar' ? '+ ' : '- '}
                          {customization.description}
                        </span>
                        {customization.price > 0 && (
                          <span className="text-foreground">
                            +R$ {(customization.price * item.quantity).toFixed(2).replace('.', ',')}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Adicionais específicos para lanches com scroll */}
                {isLancheProduct(item.product.category) && (
                  <div className="mt-4 pl-24">
                    <Label className="text-sm text-muted-foreground mb-3 block">
                      Adicionais para este lanche
                    </Label>
                    
                    <ScrollArea className="h-40 w-full border border-border rounded-md p-3 bg-muted/40">
                      {loadingExtras && extrasOptions.length === 0 ? (
                        <p className="text-xs text-muted-foreground py-2 text-center">Carregando adicionais...</p>
                      ) : extrasOptions.length === 0 ? (
                        <p className="text-xs text-muted-foreground py-2 text-center">Nenhum adicional cadastrado</p>
                      ) : (
                        <div className="space-y-2">
                          {extrasOptions.map(extra => (
                            <div key={extra.id} className="flex items-center space-x-2">
                              <Checkbox
                                id={`${item.product.id}-${extra.id}`}
                                checked={(item.extras || []).some(e => e.id === extra.id || e.name.toLowerCase() === extra.name.toLowerCase())}
                                onCheckedChange={() => handleExtraToggle(item.product.id, extra.id)}
                                className="border-border data-[state=checked]:bg-krecao-red data-[state=checked]:border-krecao-red"
                              />
                              <Label 
                                htmlFor={`${item.product.id}-${extra.id}`} 
                                className="text-sm text-foreground cursor-pointer flex-1"
                              >
                                {extra.name} (+R$ {extra.price.toFixed(2).replace('.', ',')})
                              </Label>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>

                    {/* Mostrar adicionais selecionados para este produto */}
                    {(item.extras || []).length > 0 && (
                      <div className="mt-2 pt-2 border-t border-border">
                        <p className="text-xs text-muted-foreground mb-1">Adicionais selecionados:</p>
                        {(item.extras || []).map(extra => (
                          <div key={extra.id} className="flex justify-between text-xs text-green-600 dark:text-green-400 font-medium">
                            <span>+ {extra.name}</span>
                            <span>R$ {(extra.price * item.quantity).toFixed(2).replace('.', ',')}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Observações do item */}
                <div className="mt-4">
                  <Label htmlFor={`notes-${item.product.id}`} className="text-sm text-muted-foreground">
                    Observações
                  </Label>
                  <Textarea 
                    id={`notes-${item.product.id}`} 
                    placeholder="Ex: Sem cebola, mal passado, etc." 
                    value={item.notes} 
                    onChange={e => updateNotes(item.product.id, e.target.value)} 
                    className="mt-1 bg-background border-input text-foreground" 
                  />
                </div>
              </div>
            </Card>
          ))}

          <div className="bg-card text-card-foreground p-4 rounded-lg border border-border shadow-sm">
            <div className="flex justify-between py-2 text-foreground">
              <span>Subtotal</span>
              <span className="font-semibold">R$ {getTotalPrice().toFixed(2).replace(".", ",")}</span>
            </div>
            <div className="flex justify-between py-2 text-muted-foreground">
              <span>Taxa de entrega</span>
              <span>Consultar taxa</span>
            </div>
            <Separator className="my-2 bg-border" />
            <div className="flex justify-between py-2 font-bold text-lg text-foreground">
              <span>Total</span>
              <span className="text-krecao-red">
                R$ {getTotalPrice().toFixed(2).replace(".", ",")}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartSummaryDetail;
